import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  TablePagination,
  TextField,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Visibility as ViewIcon,
  VerifiedUser as VerifiedIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { aviatorAPI } from '../../../services/api';

const AviatorBetHistory = () => {
  const [bets, setBets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalBets, setTotalBets] = useState(0);
  const [statistics, setStatistics] = useState(null);
  const [selectedBet, setSelectedBet] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  
  const [filters, setFilters] = useState({
    status: '',
    user_id: '',
    date_from: '',
    date_to: '',
  });

  useEffect(() => {
    fetchBets();
  }, [page, rowsPerPage]);

  const fetchBets = async () => {
    try {
      setLoading(true);
      const params = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      };
      
      // Apply filters
      if (filters.status) params.status = filters.status;
      if (filters.user_id) params.user_id = filters.user_id;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;

      const response = await aviatorAPI.getAllBets(params);
      if (response.data.success) {
        setBets(response.data.bets || []);
        setTotalBets(response.data.total || 0);
        setStatistics(response.data.statistics || null);
      }
    } catch (err) {
      console.error('Error fetching bets:', err);
      toast.error(err.response?.data?.message || 'Failed to fetch bets');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (bet) => {
    setSelectedBet(bet);
    setDetailsDialogOpen(true);
  };

  const handleVerifyRound = async (roundId) => {
    try {
      const response = await aviatorAPI.verifyFairness(roundId);
      if (response.data.success) {
        toast.success('Round verified successfully!');
      }
    } catch (err) {
      console.error('Error verifying round:', err);
      toast.error(err.response?.data?.message || 'Failed to verify round');
    }
  };

  const handleApplyFilters = () => {
    setPage(0);
    fetchBets();
  };

  const handleClearFilters = () => {
    setFilters({
      status: '',
      user_id: '',
      date_from: '',
      date_to: '',
    });
    setPage(0);
    setTimeout(() => {
      fetchBets();
    }, 100);
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'CASHED': return 'success';
      case 'LOST': return 'error';
      case 'OPEN': return 'warning';
      default: return 'default';
    }
  };

  const getMultiplierColor = (multiplier) => {
    const mult = parseFloat(multiplier);
    if (mult < 1.5) return 'default';
    if (mult < 2.5) return 'warning';
    if (mult < 5) return 'success';
    return 'primary';
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
            <ReceiptIcon sx={{ mr: 2 }} />
            ✈️ Aviator Bet History
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchBets}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        {/* Statistics Cards */}
        {statistics && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ background: 'linear-gradient(135deg, #667eea20 0%, #764ba220 100%)' }}>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>Total Bets</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {statistics.total_bets || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Won: {statistics.bets_won || 0} | Lost: {statistics.bets_lost || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ background: 'linear-gradient(135deg, #f093fb20 0%, #f5576c20 100%)' }}>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>Total Wagered</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {formatCurrency(statistics.total_wagered)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ background: 'linear-gradient(135deg, #4facfe20 0%, #00f2fe20 100%)' }}>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>Total Paid</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                    {formatCurrency(statistics.total_won)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ background: parseFloat(statistics.total_profit) >= 0 
                ? 'linear-gradient(135deg, #11998e20 0%, #38ef7d20 100%)'
                : 'linear-gradient(135deg, #eb575720 0%, #f4451820 100%)'
              }}>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>House Profit</Typography>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 'bold', 
                      color: parseFloat(statistics.total_profit) >= 0 ? 'success.main' : 'error.main' 
                    }}
                  >
                    {formatCurrency(statistics.total_profit)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Win Rate: {statistics.win_rate || '0%'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Search Filters
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="User ID"
                  type="number"
                  value={filters.user_id}
                  onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}
                  size="small"
                  placeholder="Filter by user"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    label="Status"
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="CASHED">Cashed (Won)</MenuItem>
                    <MenuItem value="LOST">Lost</MenuItem>
                    <MenuItem value="OPEN">Open</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="From Date"
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="To Date"
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Box display="flex" gap={1}>
                  <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    onClick={handleApplyFilters}
                    fullWidth
                    size="small"
                  >
                    Apply
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleClearFilters}
                    fullWidth
                    size="small"
                  >
                    Clear
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Bets Table */}
        <Card>
          <CardContent>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="300px">
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Bet ID</TableCell>
                        <TableCell>User</TableCell>
                        <TableCell align="center">Round #</TableCell>
                        <TableCell align="center">Crash Point</TableCell>
                        <TableCell align="right">Bet Amount</TableCell>
                        <TableCell align="center">Cashout @</TableCell>
                        <TableCell align="right">Payout</TableCell>
                        <TableCell align="right">Profit</TableCell>
                        <TableCell align="center">Status</TableCell>
                        <TableCell>Placed At</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bets.map((bet) => (
                        <TableRow key={bet.id} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              #{bet.id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {bet.username || `User${bet.user_id}`}
                            </Typography>
                            {bet.mobile && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {bet.mobile}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={`#${bet.round_number || '-'}`}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center">
                            {bet.crash_point ? (
                              <Chip
                                label={`${parseFloat(bet.crash_point).toFixed(2)}x`}
                                color={getMultiplierColor(bet.crash_point)}
                                size="small"
                                sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}
                              />
                            ) : '-'}
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 500 }}>
                              {formatCurrency(bet.amount)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {bet.cashout_multiplier ? (
                              <Chip
                                label={`${parseFloat(bet.cashout_multiplier).toFixed(2)}x`}
                                color="success"
                                size="small"
                                sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}
                              />
                            ) : (
                              bet.auto_cashout_at ? (
                                <Typography variant="caption" color="text.secondary">
                                  Auto: {parseFloat(bet.auto_cashout_at).toFixed(2)}x
                                </Typography>
                              ) : '-'
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500 }}>
                              {formatCurrency(bet.payout_amount)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: parseFloat(bet.profit) >= 0 ? 'success.main' : 'error.main',
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end'
                              }}
                            >
                              {parseFloat(bet.profit) >= 0 ? (
                                <TrendingUpIcon sx={{ fontSize: 16, mr: 0.5 }} />
                              ) : (
                                <TrendingDownIcon sx={{ fontSize: 16, mr: 0.5 }} />
                              )}
                              {formatCurrency(bet.profit)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={bet.status}
                              color={getStatusColor(bet.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {formatDateTime(bet.placed_at)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => handleViewDetails(bet)}
                                color="primary"
                              >
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            {bet.round_id && (
                              <Tooltip title="Verify Round">
                                <IconButton
                                  size="small"
                                  onClick={() => handleVerifyRound(bet.round_id)}
                                  color="success"
                                >
                                  <VerifiedIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {bets.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={11} align="center">
                            <Typography color="text.secondary" sx={{ py: 3 }}>
                              No bets found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  component="div"
                  count={totalBets}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </>
            )}
          </CardContent>
        </Card>

        {/* Details Dialog */}
        <Dialog 
          open={detailsDialogOpen} 
          onClose={() => setDetailsDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            ✈️ Bet #{selectedBet?.id} Details
          </DialogTitle>
          <DialogContent>
            {selectedBet && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Bet ID:</Typography>
                  <Typography variant="h6" gutterBottom>#{selectedBet.id}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">User:</Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedBet.username || `User${selectedBet.user_id}`}
                    {selectedBet.mobile && ` (${selectedBet.mobile})`}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Round Number:</Typography>
                  <Typography variant="body1">#{selectedBet.round_number || '-'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Crash Point:</Typography>
                  {selectedBet.crash_point ? (
                    <Chip
                      label={`${parseFloat(selectedBet.crash_point).toFixed(2)}x`}
                      color={getMultiplierColor(selectedBet.crash_point)}
                    />
                  ) : '-'}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Bet Amount:</Typography>
                  <Typography variant="body1" color="primary.main">
                    {formatCurrency(selectedBet.amount)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Status:</Typography>
                  <Chip
                    label={selectedBet.status}
                    color={getStatusColor(selectedBet.status)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Auto Cashout:</Typography>
                  <Typography variant="body1">
                    {selectedBet.auto_cashout_at ? `${parseFloat(selectedBet.auto_cashout_at).toFixed(2)}x` : 'Manual'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Cashout Multiplier:</Typography>
                  {selectedBet.cashout_multiplier ? (
                    <Chip
                      label={`${parseFloat(selectedBet.cashout_multiplier).toFixed(2)}x`}
                      color="success"
                    />
                  ) : '-'}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Payout Amount:</Typography>
                  <Typography variant="body1" color="success.main">
                    {formatCurrency(selectedBet.payout_amount)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Profit/Loss:</Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: parseFloat(selectedBet.profit) >= 0 ? 'success.main' : 'error.main',
                      fontWeight: 'bold'
                    }}
                  >
                    {formatCurrency(selectedBet.profit)}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Placed At:</Typography>
                  <Typography variant="body1">{formatDateTime(selectedBet.placed_at)}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Cashed At:</Typography>
                  <Typography variant="body1">{formatDateTime(selectedBet.cashed_at)}</Typography>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
            {selectedBet?.round_id && (
              <Button 
                variant="contained" 
                startIcon={<VerifiedIcon />}
                onClick={() => {
                  setDetailsDialogOpen(false);
                  handleVerifyRound(selectedBet.round_id);
                }}
              >
                Verify Round
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </motion.div>
  );
};

export default AviatorBetHistory;
